import type { z } from 'zod';
import { ValidationError } from '../types/errors.js';

/**
 * Parse `input` against an api-spec zod schema. On success returns the
 * validated/typed result. On failure converts the first ZodIssue into a
 * `ValidationError` with the issue's path as the field name.
 *
 * Single source of truth for input validation — reuses the same schemas
 * the server applies, so client-side validation can never drift from
 * server-side validation.
 */
export function parseParams<T>(schema: z.ZodType<T>, input: unknown): T {
  const result = schema.safeParse(input);
  if (!result.success) {
    const issue = result.error.issues[0];
    const field = issue.path.length > 0 ? issue.path.map(String).join('.') : 'params';
    throw new ValidationError(field, issue.message);
  }
  return result.data;
}

/** Resolve an explicit chain or fall back to the configured default chain. */
export function resolveChain(chain?: string, defaultChain?: string): string {
  const resolved = chain ?? defaultChain;
  if (!resolved) {
    throw new ValidationError('chain', 'No chain specified and no defaultChain configured');
  }
  return resolved;
}
